"use client";

import { c as _c } from "react/compiler-runtime";
import { useCallback, useState } from "react";
import { useRouter } from "@/i18n/navigation";
import { useNavigationState } from "@/components/navigation/navigation-provider";
import { useSearchHistory } from "./use-search-history";
import { useBodyScrollLock } from "./hooks/use-body-scroll-lock";
import { useEscapeKey } from "./hooks/use-escape-key";

/**
 * Owns the interactive state for the search overlay:
 *
 * - local `query` input state
 * - body scroll lock + Escape-to-close bindings
 * - submit / select-result / popular-term / backdrop handlers
 *
 * Split out so the overlay component stays focused on rendering and so the
 * navigation-dependent hooks (`useRouter`, history, etc.) are only wired up
 * once the overlay is actually mounted.
 */
export function useSearchOverlay() {
  const $ = _c(22);
  const {
    closeSearch
  } = useNavigationState();
  const router = useRouter();
  const searchHistory = useSearchHistory();
  const [query, setQuery] = useState("");
  let t0;
  if ($[0] !== closeSearch) {
    t0 = () => {
      setQuery("");
      closeSearch();
    };
    $[0] = closeSearch;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const handleClose = t0;
  useBodyScrollLock(true);
  useEscapeKey(true, handleClose);
  let t1;
  if ($[2] !== handleClose || $[3] !== router || $[4] !== searchHistory) {
    t1 = searchQuery => {
      const trimmed = searchQuery.trim();
      if (!trimmed) {
        return;
      }
      searchHistory.addSearch(trimmed);
      handleClose();
      router.push(`/search?q=${encodeURIComponent(trimmed)}`);
    };
    $[2] = handleClose;
    $[3] = router;
    $[4] = searchHistory;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const handleSubmit = t1;
  let t2;
  if ($[6] !== handleClose || $[7] !== router || $[8] !== searchHistory) {
    t2 = (slug, title) => {
      searchHistory.addSearch(title);
      handleClose();
      router.push(slug.startsWith("/") ? slug : `/${slug}`);
    };
    $[6] = handleClose;
    $[7] = router;
    $[8] = searchHistory;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  const handleSelectResult = t2;
  let t3;
  if ($[10] !== handleSubmit) {
    t3 = term => {
      setQuery(term);
      handleSubmit(term);
    };
    $[10] = handleSubmit;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  const handleSearchTerm = t3;
  let t4;
  if ($[12] !== handleClose) {
    t4 = e => {
      if (e.target === e.currentTarget) {
        handleClose();
      }
    };
    $[12] = handleClose;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  const handleBackdropClick = t4;
  let t5;
  if ($[14] !== handleBackdropClick || $[15] !== handleClose || $[16] !== handleSearchTerm || $[17] !== handleSelectResult || $[18] !== handleSubmit || $[19] !== query || $[20] !== searchHistory) {
    t5 = {
      query,
      setQuery,
      searchHistory,
      handleClose,
      handleSubmit,
      handleSelectResult,
      handleSearchTerm,
      handleBackdropClick
    };
    $[14] = handleBackdropClick;
    $[15] = handleClose;
    $[16] = handleSearchTerm;
    $[17] = handleSelectResult;
    $[18] = handleSubmit;
    $[19] = query;
    $[20] = searchHistory;
    $[21] = t5;
  } else {
    t5 = $[21];
  }
  return t5;
}